import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { NAV_LINKS, LOGO_URL } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { ApplyButton } from "@/components/site/AdmissionBadge";
import { telHref } from "@/lib/links";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { config } = useSiteConfig();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(10,30,78,0.97)" : "rgba(10,30,78,1)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(212,175,55,0.22)",
      }}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between gap-2 xl:gap-3 py-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="header-logo-link">
          <img src={LOGO_URL} alt="IAUE crest" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="text-[9px] 2xl:text-[10px] font-bold uppercase tracking-wide text-[#D4AF37] whitespace-nowrap">
              Ignatius Ajuru University of Education
            </div>
            <div className="font-serif text-sm 2xl:text-base font-bold text-white">School of Basic Studies</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-2 py-2 text-[13px] font-medium whitespace-nowrap transition-colors ${
                  isActive ? "text-[#D4AF37]" : "text-blue-100/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <a href={telHref(config.phonePrimary)} className="btn-outline-gold !px-3 !py-2 !text-xs whitespace-nowrap hidden lg:inline-flex xl:hidden 2xl:inline-flex" data-testid="header-call-button">
            <Phone size={14} /> {config.phonePrimary}
          </a>
          <ApplyButton className="btn-red !px-4 !py-2.5 !text-xs whitespace-nowrap" testId="header-apply-button" />
        </div>

        <button
          className="xl:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden overflow-hidden border-t border-amber-500/20 bg-[#0A1E4E]"
            data-testid="mobile-menu"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-medium border-b border-white/5 ${
                      isActive ? "text-[#D4AF37]" : "text-blue-100/90"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/admin" className="px-3 py-3 text-sm text-blue-200/60 flex items-center gap-2" data-testid="mobile-admin-link">
                <ShieldCheck size={15} /> Admin Portal
              </Link>
              <div className="flex gap-2 pt-3">
                <a href={telHref(config.phonePrimary)} className="btn-outline-gold flex-1 !py-3" data-testid="mobile-header-call">
                  <Phone size={15} /> Call
                </a>
                <ApplyButton className="btn-red flex-1 !py-3" testId="mobile-header-apply" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
